import { SpaceData, RoomData } from '@/components/SpacePlanningPanel/SpacePlanningTypes';
import { materialService } from '@/services/material.service';
import { AnalysisResult } from '@/services/score/noise';
import { calculateFinalBioScoreForRoom } from '@/services/score/bioScore';
import {
	calculateMaterialScoreFromIds,
	getMaterialRecommendationForScore,
	MaterialScoreDetails,
} from '@/services/score/material';
import {
	calculateSunlightScore,
} from '@/services/score/sunlight';
import {
	calculateViewScore,
} from '@/services/score/view';
import { unitConversionService } from '@/services/unit-conversion.service';
import { useDailySunlightStore } from '@/store/dailySunlightsStore';
import { useSpacePlanningStore } from '@/store/spacePlanningStore';
import { useViewScoreStore } from '@/store/viewScoreStore';
import {
	normalizePaletteState,
	PALETTE_WORKFLOW_SECTION,
	WorkflowStage,
} from '@/types/palette';
import { WorkflowDataMap } from '@/types/workflow';

type SpacesWithRooms = Record<string, SpaceData & { rooms: RoomData[] }>;

export interface ReportImageData {
	label: string;
	fileName: string;
	mimeType: string;
	dataUrl: string;
}

export interface PersistedExportAssets {
	windowsImages?: ReportImageData[];
	daylightImages?: ReportImageData[];
}

export interface BioScoreRoomOverviewRow {
	functionName: string;
	roomName: string;
	roomArea: string;
	count: number;
	wallMaterial: string;
	floorMaterial: string;
	ceilingMaterial: string;
	materialSource: string;
}

export interface BioScoreResultsRow {
	functionName: string;
	roomName: string;
	material: string;
	view: string;
	daylight: string;
	bonus: string;
	total: string;
}

export interface BioScoreFunctionProxyRow {
	functionName: string;
	functionArea: string;
	paletteName: string;
	proxyScore: string;
}

export interface BioScoreViewBreakdownRow {
	functionName: string;
	roomName: string;
	total: string;
	open: string;
	vegetation: string;
	water: string;
	skyView: string;
	diversity: string;
	horizontal: string;
	recommendation: string;
}

export interface BioScoreMaterialBreakdownRow {
	functionName: string;
	roomName: string;
	total: string;
	wall: string;
	floor: string;
	ceiling: string;
	wallMaterial: string;
	floorMaterial: string;
	ceilingMaterial: string;
	source: string;
	recommendation: string;
}

export interface BioScoreDaylightBreakdownRow {
	functionName: string;
	roomName: string;
	total: string;
	winter: string;
	summer: string;
	springFall: string;
	consistency: string;
	quality: string;
	averageHours: string;
	recommendation: string;
}

export interface BioScoreReportData {
	title: string;
	generatedAt: string;
	stage: {
		gate: string;
		standardsLine: string;
		description: string;
	};
	noise: {
		weightedScore: string;
		targetNoise: string;
		exteriorNoise: string;
		interiorNoise: string;
		intendedFinalUse: string;
		recommendation: string;
	} | null;
	summary: {
		averageBioScore: string;
		baseAverage: string;
		noiseAdjustment: string;
		mode: 'room' | 'function_proxy' | 'empty';
		roomCount: number;
	};
	roomOverviewRows: BioScoreRoomOverviewRow[];
	resultsRows: BioScoreResultsRow[];
	functionRows: BioScoreFunctionProxyRow[];
	materialBreakdownRows: BioScoreMaterialBreakdownRow[];
	viewBreakdownRows: BioScoreViewBreakdownRow[];
	daylightBreakdownRows: BioScoreDaylightBreakdownRow[];
	recommendations: { title: string; text: string }[];
	images: ReportImageData[];
}

const STAGE_META: Record<
	WorkflowStage,
	{ gate: string; standardsLine: string; description: string }
> = {
	predesign_programming: {
		gate: 'Gate 1',
		standardsLine: 'Massing Studies',
		description:
			'Results are directional and assumption-based. Function-level palettes and high-level environmental inputs drive the output.',
	},
	schematic: {
		gate: 'Gate 2',
		standardsLine: 'Informed Design Studies',
		description:
			'Results combine function assumptions with floor plate and opening studies, but may still be incomplete at room level.',
	},
	design_development: {
		gate: 'Gate 3',
		standardsLine: 'Targeted Design Studies',
		description:
			'Results are room-led and closer to client-facing validation, with explicit room materials and detailed analysis where available, otherwise palette assumptions are used as fallback.',
	},
};

const mergeRoomsBySpaceId = (
	baseRooms: RoomData[] = [],
	overlayRooms: RoomData[] = []
): RoomData[] => {
	const mergedById = new Map<string, RoomData>();
	baseRooms.forEach((room) => mergedById.set(String(room.spaceId), room));
	overlayRooms.forEach((room) => {
		const roomId = String(room.spaceId);
		const existing = mergedById.get(roomId);
		mergedById.set(roomId, existing ? { ...existing, ...room } : room);
	});
	return Array.from(mergedById.values());
};

const mergeSpacesData = (
	baseSpaces: SpacesWithRooms | undefined,
	overlaySpaces: SpacesWithRooms | undefined
): SpacesWithRooms | undefined => {
	if (!baseSpaces && !overlaySpaces) return undefined;
	if (!baseSpaces) return overlaySpaces;
	if (!overlaySpaces) return baseSpaces;

	const merged: SpacesWithRooms = { ...baseSpaces };

	Object.entries(overlaySpaces).forEach(([functionKey, overlaySpace]) => {
		const baseSpace = merged[functionKey];
		if (!baseSpace) {
			merged[functionKey] = overlaySpace;
			return;
		}

		merged[functionKey] = {
			...baseSpace,
			...overlaySpace,
			rooms: mergeRoomsBySpaceId(baseSpace.rooms || [], overlaySpace.rooms || []),
		};
	});

	return merged;
};

const createCanonicalRoomMatcher = () => {
	const spaces = useSpacePlanningStore.getState().spaces;
	const canonicalRoomIds = new Set<string>();

	Object.entries(spaces || {}).forEach(([functionKey, space]) => {
		(space.rooms || []).forEach((room) => {
			canonicalRoomIds.add(`${functionKey}::${String(room.spaceId)}`);
		});
	});

	return (functionKey: string, roomId: string | number) =>
		canonicalRoomIds.has(`${functionKey}::${String(roomId)}`);
};

const hasExplicitMaterialSelection = (room: RoomData): boolean => {
	return Boolean(room.materials?.wall && room.materials?.floor && room.materials?.ceiling);
};

const formatScore = (value: number | undefined | null): string => {
	if (typeof value !== 'number' || Number.isNaN(value)) return 'N/A';
	return value.toFixed(2);
};

const formatArea = (value: number | undefined | null): string => {
	if (typeof value !== 'number' || Number.isNaN(value) || value <= 0) return 'N/A';
	return unitConversionService.formatWithPreferredUnit(value, 'area', 'm2');
};

const getMaterialName = (id?: string) => {
	if (!id) return 'Not selected';
	return materialService.getMaterialById(id)?.name ?? 'Unknown material';
};

const getRoomLabelWithLevel = (room: RoomData): string => {
	const baseLabel = room.spaceDescription || `Room ${room.spaceId}`;
	return typeof room.level === 'number' ? `Level ${room.level} - ${baseLabel}` : baseLabel;
};

const getMaterialSourceLabel = (
	source: 'explicit' | 'palette_assumptions' | 'palette_baseline' | undefined
) => {
	if (source === 'explicit') return 'Explicit room materials';
	if (source === 'palette_assumptions') return 'Palette material assumptions';
	if (source === 'palette_baseline') return 'Palette baseline score';
	return 'Not available';
};

const formatNoiseFinalUse = (value: string): string => {
	return value
		.split(/[_-]/g)
		.filter(Boolean)
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(' ');
};

const getViewRecommendationText = (input: {
	totalScore: number;
	openScore: number;
	vegetationScore: number;
	skyViewFactor: number;
	horizontalOpenness: number;
	viewDiversity: number;
}): string => {
	const issues: string[] = [];
	if (input.openScore / 30 < 0.5) {
		issues.push('increase open sky views');
	}
	if (input.vegetationScore / 20 < 0.4) {
		issues.push('add more greenery visibility');
	}
	if (input.skyViewFactor / 10 < 0.5) {
		issues.push('improve overhead openness for natural light');
	}
	if (input.horizontalOpenness / 10 < 0.5) {
		issues.push('reduce eye-level obstructions');
	}
	if (input.viewDiversity / 7 < 0.4) {
		issues.push('enhance visual variety');
	}

	if (input.totalScore >= 7.0) {
		return 'Excellent view quality with great openness, natural elements, and visual diversity.';
	}
	if (input.totalScore >= 5.0) {
		if (issues.length > 0) {
			return `Good view quality. To improve: ${issues.slice(0, 2).join(', ')}.`;
		}
		return 'Good view quality with balanced natural elements and openness.';
	}
	if (input.totalScore >= 3.0) {
		if (issues.length > 0) {
			return `Moderate view quality. Focus on: ${issues.slice(0, 2).join(', ')}.`;
		}
		return 'Moderate view quality. Consider enhancing openness and diversity.';
	}
	if (issues.length > 0) {
		return `Limited view quality. Priority improvements: ${issues.slice(0, 3).join(', ')}.`;
	}
	return 'Limited view quality. Significant improvements needed in openness and horizontal views.';
};

const getSunlightRecommendationText = (input: {
	totalScore: number;
	winterScore: number;
	summerScore: number;
	springFallScore: number;
	consistencyScore: number;
	qualityScore: number;
}): string => {
	const issues: string[] = [];
	if (input.winterScore / 30 < 0.4) {
		issues.push('improve winter sunlight (most critical)');
	}
	if (input.summerScore / 25 < 0.5) {
		issues.push('optimize summer exposure');
	}
	if (input.springFallScore / 25 < 0.5) {
		issues.push('enhance spring/fall daylight');
	}
	if (input.consistencyScore / 10 < 0.5) {
		issues.push('increase year-round consistency');
	}
	if (input.qualityScore / 10 < 0.5) {
		issues.push('extend daylight duration');
	}

	if (input.totalScore >= 7.0) {
		return 'Excellent daylight exposure throughout the year with good consistency and duration.';
	}
	if (input.totalScore >= 5.0) {
		if (issues.length > 0) {
			return `Good daylight levels. To improve: ${issues.slice(0, 2).join(', ')}.`;
		}
		return 'Good daylight levels with balanced seasonal exposure.';
	}
	if (input.totalScore >= 3.0) {
		if (issues.length > 0) {
			return `Moderate daylight. Focus on: ${issues.slice(0, 2).join(', ')}.`;
		}
		return 'Moderate daylight. Consider increasing window area or adjusting orientation.';
	}
	if (issues.length > 0) {
		return `Insufficient daylight. Priority: ${issues.slice(0, 3).join(', ')}.`;
	}
	return 'Insufficient daylight. Significant improvements needed in window size, placement, or orientation.';
};

const buildRoomRows = (
	spacesData: SpacesWithRooms | undefined,
	stage: WorkflowStage,
	functionAssignments: Record<string, string>,
	typologyAssignments: Record<string, string>,
	paletteById: Record<string, ReturnType<typeof normalizePaletteState>['palettes'][number]>,
	isCanonicalRoom: (functionKey: string, roomId: string | number) => boolean
) => {
	if (!spacesData) {
		return {
			roomOverviewRows: [] as BioScoreRoomOverviewRow[],
			resultsRows: [] as BioScoreResultsRow[],
		};
	}

	const roomOverviewRows: BioScoreRoomOverviewRow[] = [];
	const resultsRows: BioScoreResultsRow[] = [];

	Object.entries(spacesData).forEach(([functionKey, space]) => {
		const functionName = space.name || functionKey;

		space.rooms.forEach((room) => {
			if (!isCanonicalRoom(functionKey, room.spaceId)) return;

			const levelScopedTypologyAreaKey = room.spaceDescription
				? typeof room.level === 'number'
					? `${functionKey}::L${room.level}::${room.spaceDescription}`
					: `${functionKey}::${room.spaceDescription}`
				: undefined;
			const typologyAreaKey = room.spaceDescription
				? `${functionKey}::${room.spaceDescription}`
				: undefined;
			const areaPaletteId =
				stage === 'predesign_programming'
					? undefined
					: (levelScopedTypologyAreaKey
							? typologyAssignments[levelScopedTypologyAreaKey]
							: undefined) ||
						(typologyAreaKey ? typologyAssignments[typologyAreaKey] : undefined) ||
						(room.spaceDescription ? typologyAssignments[room.spaceDescription] : undefined);
			const functionPaletteId = functionAssignments[functionKey];
			const assignedPaletteId = areaPaletteId || functionPaletteId;
			const assignedPalette = assignedPaletteId ? paletteById[assignedPaletteId] : undefined;
			const assumptions = assignedPalette?.materialAssumptions;
			const fallbackMaterialBreakdown = calculateMaterialScoreFromIds(
				{
					wallMaterialId: assumptions?.wall,
					floorMaterialId: assumptions?.floor,
					ceilingMaterialId: assumptions?.ceiling,
					wallArea: room.wallAreaTotal ?? 0,
					floorArea: room.ASF ?? 0,
					ceilingArea: room.ASF ?? 0,
				},
				(id) => materialService.getMaterialById(id)
			);
			const fallbackMaterialScore =
				fallbackMaterialBreakdown?.score ?? assignedPalette?.baselineScore;

			const shouldApplyFallback =
				!hasExplicitMaterialSelection(room) &&
				(typeof room.scoreBreakdown?.materials !== 'number' ||
					room.scoreBreakdown?.materials <= 0) &&
				typeof fallbackMaterialScore === 'number';

			const materialSource = hasExplicitMaterialSelection(room)
				? 'explicit'
				: fallbackMaterialBreakdown
					? 'palette_assumptions'
					: shouldApplyFallback
						? 'palette_baseline'
						: undefined;

			const roomForScoring: RoomData = shouldApplyFallback
				? {
						...room,
						scoreBreakdown: {
							...room.scoreBreakdown,
							materials: fallbackMaterialScore,
						},
					}
				: room;

			const { displayedScore } = calculateFinalBioScoreForRoom(roomForScoring);
			const roomName = getRoomLabelWithLevel(roomForScoring);
			const wallMaterial =
				materialSource === 'explicit'
					? getMaterialName(room.materials?.wall)
					: materialSource === 'palette_assumptions'
						? getMaterialName(assumptions?.wall)
						: 'Preset baseline only';
			const floorMaterial =
				materialSource === 'explicit'
					? getMaterialName(room.materials?.floor)
					: materialSource === 'palette_assumptions'
						? getMaterialName(assumptions?.floor)
						: 'Preset baseline only';
			const ceilingMaterial =
				materialSource === 'explicit'
					? getMaterialName(room.materials?.ceiling)
					: materialSource === 'palette_assumptions'
						? getMaterialName(assumptions?.ceiling)
						: 'Preset baseline only';

			roomOverviewRows.push({
				functionName,
				roomName,
				roomArea: formatArea((roomForScoring.ASF || 0) * (roomForScoring.quantity || 1)),
				count: roomForScoring.quantity,
				wallMaterial,
				floorMaterial,
				ceilingMaterial,
				materialSource: getMaterialSourceLabel(materialSource),
			});

			resultsRows.push({
				functionName,
				roomName,
				material: formatScore(roomForScoring.scoreBreakdown?.materials),
				view: formatScore(roomForScoring.scoreBreakdown?.view),
				daylight: formatScore(roomForScoring.scoreBreakdown?.sunlight),
				bonus: formatScore(roomForScoring.scoreBreakdown?.bonus),
				total: formatScore(displayedScore),
			});
		});
	});

	return { roomOverviewRows, resultsRows };
};

const buildFunctionRows = (
	spacesData: SpacesWithRooms | undefined,
	functionAssignments: Record<string, string>,
	paletteById: Record<string, ReturnType<typeof normalizePaletteState>['palettes'][number]>
) => {
	if (!spacesData) return [];
	const canonicalSpaces = useSpacePlanningStore.getState().spaces;

	return Object.entries(functionAssignments)
		.map(([functionKey, paletteId]) => {
			const palette = paletteById[paletteId];
			if (!palette) return null;
			const canonicalRooms = canonicalSpaces[functionKey]?.rooms || [];
			const functionArea = canonicalRooms.reduce(
				(sum, room) => sum + (room.ASF || 0) * (room.quantity || 1),
				0
			);

			return {
				functionName: spacesData[functionKey]?.name || functionKey,
				functionArea: formatArea(functionArea),
				paletteName: palette.name,
				proxyScore: formatScore(palette.baselineScore),
			};
		})
		.filter((row): row is BioScoreFunctionProxyRow => row !== null);
};

const buildAnalysisBreakdownRows = (spacesData: SpacesWithRooms | undefined) => {
	const viewStoreState = useViewScoreStore.getState();
	const { horizonContexts, isBeyondHorizonEnabled, viewData } = viewStoreState;
	const dailySunlightState = useDailySunlightStore.getState();

	const materialRows: BioScoreMaterialBreakdownRow[] = [];
	const viewRows: BioScoreViewBreakdownRow[] = [];
	const daylightRows: BioScoreDaylightBreakdownRow[] = [];
	const resolveMaterial = (id: string) => materialService.getMaterialById(id);

	if (spacesData) {
		const isCanonicalRoom = createCanonicalRoomMatcher();
		Object.entries(spacesData).forEach(([functionKey, space]) => {
			const functionName = space.name || functionKey;

			space.rooms.forEach((room) => {
				if (!isCanonicalRoom(functionKey, room.spaceId)) return;

				const roomName = getRoomLabelWithLevel(room);

				let materialBreakdown: MaterialScoreDetails | undefined;
				let source = 'Unavailable';
				let wallMaterial = 'Not selected';
				let floorMaterial = 'Not selected';
				let ceilingMaterial = 'Not selected';

				if (room.materials?.wall && room.materials?.floor && room.materials?.ceiling) {
					materialBreakdown = calculateMaterialScoreFromIds(
						{
							wallMaterialId: room.materials.wall,
							floorMaterialId: room.materials.floor,
							ceilingMaterialId: room.materials.ceiling,
							wallArea: room.wallAreaTotal ?? 0,
							floorArea: room.ASF ?? 0,
							ceilingArea: room.ASF ?? 0,
						},
						resolveMaterial
					);
					source = 'Explicit room materials';
					wallMaterial = getMaterialName(room.materials.wall);
					floorMaterial = getMaterialName(room.materials.floor);
					ceilingMaterial = getMaterialName(room.materials.ceiling);
				}

				if (materialBreakdown) {
					materialRows.push({
						functionName,
						roomName,
						total: formatScore(materialBreakdown.score),
						wall: formatScore(materialBreakdown.breakdown.wall.bioScore),
						floor: formatScore(materialBreakdown.breakdown.floor.bioScore),
						ceiling: formatScore(materialBreakdown.breakdown.ceiling.bioScore),
						wallMaterial,
						floorMaterial,
						ceilingMaterial,
						source,
						recommendation: materialBreakdown.recommendation,
					});
				}

				if (room.view) {
					const breakdown = calculateViewScore(
						room.view,
						horizonContexts,
						isBeyondHorizonEnabled
					);
					viewRows.push({
						functionName,
						roomName,
						total: formatScore(breakdown.totalScore),
						open: formatScore(breakdown.openScore),
						vegetation: formatScore(breakdown.vegetationScore),
						water: formatScore(breakdown.waterScore),
						skyView: formatScore(breakdown.skyViewFactor),
						diversity: formatScore(breakdown.viewDiversity),
						horizontal: formatScore(breakdown.horizontalOpenness),
						recommendation: getViewRecommendationText({
							totalScore: breakdown.totalScore,
							openScore: breakdown.openScore,
							vegetationScore: breakdown.vegetationScore,
							skyViewFactor: breakdown.skyViewFactor,
							horizontalOpenness: breakdown.horizontalOpenness,
							viewDiversity: breakdown.viewDiversity,
						}),
					});
				}

				if (room.sunlight) {
					const breakdown = calculateSunlightScore(room.sunlight);
					daylightRows.push({
						functionName,
						roomName,
						total: formatScore(breakdown.totalScore),
						winter: formatScore(breakdown.winterScore),
						summer: formatScore(breakdown.summerScore),
						springFall: formatScore(breakdown.springFallScore),
						consistency: formatScore(breakdown.consistencyScore),
						quality: formatScore(breakdown.qualityScore),
						averageHours: formatScore(breakdown.averageHours),
						recommendation: getSunlightRecommendationText({
							totalScore: breakdown.totalScore,
							winterScore: breakdown.winterScore,
							summerScore: breakdown.summerScore,
							springFallScore: breakdown.springFallScore,
							consistencyScore: breakdown.consistencyScore,
							qualityScore: breakdown.qualityScore,
						}),
					});
				}
			});
		});
	}

	if (viewRows.length === 0 && viewData && Object.keys(viewData).length > 0) {
		Object.values(viewData).forEach((statsMap, index) => {
			const breakdown = calculateViewScore(statsMap, horizonContexts, isBeyondHorizonEnabled);
			viewRows.push({
				functionName: 'Gate Analysis',
				roomName: `View Point ${index + 1}`,
				total: formatScore(breakdown.totalScore),
				open: formatScore(breakdown.openScore),
				vegetation: formatScore(breakdown.vegetationScore),
				water: formatScore(breakdown.waterScore),
				skyView: formatScore(breakdown.skyViewFactor),
				diversity: formatScore(breakdown.viewDiversity),
				horizontal: formatScore(breakdown.horizontalOpenness),
				recommendation: getViewRecommendationText({
					totalScore: breakdown.totalScore,
					openScore: breakdown.openScore,
					vegetationScore: breakdown.vegetationScore,
					skyViewFactor: breakdown.skyViewFactor,
					horizontalOpenness: breakdown.horizontalOpenness,
					viewDiversity: breakdown.viewDiversity,
				}),
			});
		});
	}

	if (daylightRows.length === 0) {
		const gridPointBatches = dailySunlightState.sunlightResultsGridPoints || [];
		const fromGridPoints = gridPointBatches.flat();
		const fromRooms = dailySunlightState.sunlightResultsRooms || [];
		const fromDetailed = dailySunlightState.detailedGridPointsData || [];
		const mergedSunlightData = [...fromRooms, ...fromGridPoints, ...fromDetailed];
		const uniqueMap = new Map<string, (typeof mergedSunlightData)[number]>();

		mergedSunlightData.forEach((entry, index) => {
			const pointKey = entry.point
				? `${entry.point.x.toFixed(3)}-${entry.point.y.toFixed(3)}-${entry.point.z.toFixed(3)}`
				: `no-point-${index}`;
			const roomKey = entry.roomName || 'unnamed-room';

			const metricsKey = [
				entry.averageDailySunlightHours,
				entry.winterMonthsAverage,
				entry.summerMonthsAverage,
				entry.springMonthsAverage,
				entry.fallMonthsAverage,
				entry.daylightAutonomyPercent,
				entry.consistencyScore,
			]
				.map((value) => Number(value ?? 0).toFixed(3))
				.join('|');
			const key = `${roomKey}-${pointKey}-${metricsKey}`;
			if (!uniqueMap.has(key)) {
				uniqueMap.set(key, entry);
			}
		});

		const fallbackSunlightData = Array.from(uniqueMap.values());
		const groupedByRoomName = fallbackSunlightData.reduce<
			Record<string, typeof fallbackSunlightData>
		>((acc, item) => {
			const key = item.roomName || 'Grid Points';
			if (!acc[key]) {
				acc[key] = [];
			}
			acc[key].push(item);
			return acc;
		}, {});

		Object.entries(groupedByRoomName).forEach(([roomName, values], index) => {
			const breakdown = calculateSunlightScore(values);
			daylightRows.push({
				functionName: 'Gate Analysis',
				roomName: roomName || `Sun Group ${index + 1}`,
				total: formatScore(breakdown.totalScore),
				winter: formatScore(breakdown.winterScore),
				summer: formatScore(breakdown.summerScore),
				springFall: formatScore(breakdown.springFallScore),
				consistency: formatScore(breakdown.consistencyScore),
				quality: formatScore(breakdown.qualityScore),
				averageHours: formatScore(breakdown.averageHours),
				recommendation: getSunlightRecommendationText({
					totalScore: breakdown.totalScore,
					winterScore: breakdown.winterScore,
					summerScore: breakdown.summerScore,
					springFallScore: breakdown.springFallScore,
					consistencyScore: breakdown.consistencyScore,
					qualityScore: breakdown.qualityScore,
				}),
			});
		});
	}

	return { materialRows, viewRows, daylightRows };
};

const buildRecommendations = (
	stage: WorkflowStage,
	resultsRows: BioScoreResultsRow[],
	functionRows: BioScoreFunctionProxyRow[],
	summary: BioScoreReportData['summary'],
	materialRows: BioScoreMaterialBreakdownRow[],
	noiseData: AnalysisResult | undefined,
	viewRows: BioScoreViewBreakdownRow[],
	daylightRows: BioScoreDaylightBreakdownRow[]
) => {
	const recommendations: BioScoreReportData['recommendations'] = [
		{ title: 'Stage Context', text: STAGE_META[stage].description },
	];

	if (summary.mode === 'function_proxy' && functionRows.length > 0) {
		recommendations.push({
			title: 'Function Proxy',
			text: 'This export is based on function-level palette assumptions because no rooms are currently defined.',
		});
	}

	if (resultsRows.some((row) => row.material === 'N/A')) {
		recommendations.push({
			title: 'Materials',
			text: 'Some rooms are missing explicit material scores. Complete room materials or palette assumptions to improve report accuracy.',
		});
	} else if (materialRows.length > 0) {
		const averageMaterialScore =
			materialRows.reduce((sum, row) => sum + Number(row.total || 0), 0) /
			materialRows.length;
		recommendations.push({
			title: 'Materials',
			text: getMaterialRecommendationForScore(averageMaterialScore),
		});
	} else if (resultsRows.some((row) => Number(row.material) < 5)) {
		recommendations.push({
			title: 'Materials',
			text: getMaterialRecommendationForScore(
				resultsRows.reduce((sum, row) => sum + Number(row.material || 0), 0) /
					resultsRows.length
			),
		});
	}

	if (viewRows.length === 0) {
		recommendations.push({
			title: 'View / Windows',
			text: 'View analysis is incomplete. Run the view workflow to capture window-related quality and openness in the report.',
		});
	} else {
		const averageView = viewRows.reduce((sum, row) => sum + Number(row.total), 0) / viewRows.length;
		const averageOpen = viewRows.reduce((sum, row) => sum + Number(row.open), 0) / viewRows.length;
		const averageVegetation =
			viewRows.reduce((sum, row) => sum + Number(row.vegetation), 0) / viewRows.length;
		const averageSkyView =
			viewRows.reduce((sum, row) => sum + Number(row.skyView), 0) / viewRows.length;
		const averageDiversity =
			viewRows.reduce((sum, row) => sum + Number(row.diversity), 0) / viewRows.length;
		const averageHorizontal =
			viewRows.reduce((sum, row) => sum + Number(row.horizontal), 0) / viewRows.length;

		const viewRecommendation = getViewRecommendationText({
			totalScore: averageView,
			openScore: averageOpen,
			vegetationScore: averageVegetation,
			skyViewFactor: averageSkyView,
			horizontalOpenness: averageHorizontal,
			viewDiversity: averageDiversity,
		});
		recommendations.push({
			title: 'View / Windows',
			text: viewRecommendation,
		});
	}

	if (daylightRows.length === 0) {
		recommendations.push({
			title: 'Daylight',
			text: 'Daylight analysis is incomplete. Run daylight studies to improve confidence in room-level performance.',
		});
	} else {
		const averageDaylight = daylightRows.reduce((sum, row) => sum + Number(row.total), 0) / daylightRows.length;
		const averageWinter =
			daylightRows.reduce((sum, row) => sum + Number(row.winter), 0) / daylightRows.length;
		const averageSummer =
			daylightRows.reduce((sum, row) => sum + Number(row.summer), 0) / daylightRows.length;
		const averageSpringFall =
			daylightRows.reduce((sum, row) => sum + Number(row.springFall), 0) /
			daylightRows.length;
		const averageConsistency =
			daylightRows.reduce((sum, row) => sum + Number(row.consistency), 0) /
			daylightRows.length;
		const averageQuality =
			daylightRows.reduce((sum, row) => sum + Number(row.quality), 0) / daylightRows.length;
		const daylightRecommendation = getSunlightRecommendationText({
			totalScore: averageDaylight,
			winterScore: averageWinter,
			summerScore: averageSummer,
			springFallScore: averageSpringFall,
			consistencyScore: averageConsistency,
			qualityScore: averageQuality,
		});
		recommendations.push({
			title: 'Daylight',
			text: daylightRecommendation,
		});
	}

	if (noiseData) {
		recommendations.push({
			title: 'Noise',
			text: noiseData.recommendation,
		});
	}

	const lowRooms = resultsRows.filter((row) => Number(row.total) < 6).slice(0, 3);
	if (lowRooms.length > 0) {
		recommendations.push({
			title: 'Priority Rooms',
			text: `Priority review rooms: ${lowRooms
				.map((row) => `${row.functionName} / ${row.roomName}`)
				.join(', ')}.`,
		});
	}

	if (recommendations.length === 1) {
		recommendations.push({
			title: 'General',
			text: 'No critical gaps detected in the available workflow state. Use this report as a snapshot for design discussion and comparison.',
		});
	}

	return recommendations;
};

export const buildBioScoreReportData = ({
	workflowData,
	currentStage,
	images,
}: {
	workflowData: WorkflowDataMap;
	currentStage: WorkflowStage;
	images: ReportImageData[];
}): BioScoreReportData => {
	const section2Data = workflowData[2]?.data as SpacesWithRooms | undefined;
	const section4Data = workflowData[4]?.data as SpacesWithRooms | undefined;
	const spacesData = mergeSpacesData(section2Data, section4Data);
	const noiseData = workflowData[3]?.data as AnalysisResult | undefined;
	const summaryData = workflowData[5]?.data as
		| {
				rooms?: number;
				roomAverage?: number;
				noiseAdjustment?: number;
				averageBioScore?: number;
				mode?: 'function_proxy';
			}
		| undefined;
	const paletteState = normalizePaletteState(workflowData[PALETTE_WORKFLOW_SECTION]?.data);
	const paletteById = Object.fromEntries(
		paletteState.palettes.map((palette) => [palette.id, palette])
	);
	const stageMeta = STAGE_META[currentStage];
	const isCanonicalRoom = createCanonicalRoomMatcher();
	const { roomOverviewRows, resultsRows } = buildRoomRows(
		spacesData,
		currentStage,
		paletteState.functionAssignments,
		paletteState.typologyAssignments,
		paletteById,
		isCanonicalRoom
	);
	const functionRows = buildFunctionRows(
		spacesData,
		paletteState.functionAssignments,
		paletteById
	);
	const { materialRows, viewRows, daylightRows } = buildAnalysisBreakdownRows(spacesData);

	const mode: BioScoreReportData['summary']['mode'] =
		summaryData?.mode === 'function_proxy'
			? 'function_proxy'
			: resultsRows.length > 0
				? 'room'
				: 'empty';

	const summary: BioScoreReportData['summary'] = {
		averageBioScore: formatScore(summaryData?.averageBioScore),
		baseAverage: formatScore(summaryData?.roomAverage),
		noiseAdjustment: formatScore(summaryData?.noiseAdjustment),
		mode,
		roomCount: summaryData?.rooms ?? roomOverviewRows.length,
	};

	return {
		title: `BioScore ${stageMeta.gate} Report`,
		generatedAt: new Intl.DateTimeFormat('en-GB', {
			dateStyle: 'medium',
			timeStyle: 'short',
		}).format(new Date()),
		stage: stageMeta,
		noise: noiseData
			? {
					weightedScore: formatScore(noiseData.weightedScore),
					targetNoise: `${noiseData.targetNoise} dBA`,
					exteriorNoise: `${Math.round(noiseData.exteriorNoise)} dBA`,
					interiorNoise: `${Math.round(noiseData.interiorNoise)} dBA`,
					intendedFinalUse: formatNoiseFinalUse(noiseData.spaceType),
					recommendation: noiseData.recommendation,
				}
			: null,
		summary,
		roomOverviewRows,
		resultsRows,
		functionRows,
		materialBreakdownRows: materialRows,
		viewBreakdownRows: viewRows,
		daylightBreakdownRows: daylightRows,
		recommendations: buildRecommendations(
			currentStage,
			resultsRows,
			functionRows,
			summary,
			materialRows,
			noiseData,
			viewRows,
			daylightRows
		),
		images,
	};
};
